// Copyright (c) Docugami, Inc. All rights reserved.

import { ComponentId } from "sdv/engine/Component";
import { IDrawCommand, IRenderer, IRendererInput, Queries } from "../engine/Engine";
import { CanvasHelperStatics } from "components/doc_hosting/doc_hosts/web/CanvasHelperStatics";
import { Box, boxesEqual } from "store/shared_types";
import { TokenRenderComponent } from "sdv/components/TokenRenderComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";

/**
 * Renders renderable tokens to the canvas.
 */
export class TokenRenderer implements IRenderer {
    get renderEnabled(): boolean { return true; }

    queries: Queries = {
        tokenRender: { types: [ComponentId.TokenRender, ComponentId.TokenRange]}
    }

    render(engine: IRendererInput): IDrawCommand[] {
        const commands: IDrawCommand[] = []

        const tokenRenderEntities = this.queries.tokenRender.query!.entities;

        if (!tokenRenderEntities?.length) {
            // Nothing to do
            return commands;
        }

        const tokenLookup = engine.resources.get("pageTokens").value;
        const visiblePages = engine.resources.get("visiblePages").value;

        tokenRenderEntities.forEach(tokenRenderEntity => {
            const tokenRenderComp = tokenRenderEntity.getComponent<TokenRenderComponent>(ComponentId.TokenRender)!;
            const tokenRange = tokenRenderEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;

            for (let tok = tokenRange.fromTok; tok < tokenRange.toTok; tok++) {
                const tokenBox = tokenLookup[tok];
                if (!tokenBox) {
                    continue;
                }

                if (!visiblePages.find(p => p.pageIndex === tokenBox.page)) {
                    if (tokenBox.page > visiblePages[visiblePages.length - 1].pageIndex) {
                        // The current box is past the last visible page,
                        // so no need to continue iterating through the tokens
                        break;
                    }
                    continue;
                }

                if (boxesEqual(tokenBox, tokenLookup[tok-1])) {
                    // Duplicate bounds
                    continue;
                }

                const color = tokenRenderComp.tokenColor[tok] ?? tokenRenderComp.defaultColor;
                commands.push( context => drawToken(context, tokenBox, color));
            }
        })

        return commands;
    }
}

function drawToken(context: CanvasRenderingContext2D, box: Box, color: string): void {
    CanvasHelperStatics.fillRectangle(
        context,
        box,
        color,
        true);
}
